import { ENDPOINT_TEXT_MESSAGE_NAME } from '../../../modules/API/constants';
import { CONFERENCE_LEFT, ENDPOINT_MESSAGE_RECEIVED } from '../base/conference/actionTypes';
import { getCurrentConference } from '../base/conference/functions';
import { FAKE_PARTICPANT_LEFT } from '../base/mtApi/actionTypes';
import { getLocalParticipant, getParticipantDisplayName } from '../base/participants/functions';
import { MiddlewareRegistry } from '../base/redux';
import { showNotification } from '../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';

import { CLEAR_REMOTE_ANNOTATIONS, SET_REMOTE_ANNOTATION_ALLOWED } from './actionTypes';
import { setRemoteAnnotationAllowed, showLocalShareWithAnnotations } from './actions';
import {
    ANNOTATION_MESSAGE,
    ANNOTATION_OFF_PROPERTY,
    DESKTOP_BRIDGE_MESSAGE,
    MAX_POINTS_PER_MESSAGE,
    POINTER_MESSAGE
} from './constants';
import {
    getAnnotationColor,
    isRemoteAnnotationAllowed,
    readPoints,
    sendAnnotationMessage
} from './functions';
import { addSegment, clearShare, removePointer, setPointer } from './strokes';

/**
 * Viewers the local sharer was already told about during the current share.
 */
const _notified = new Set();

MiddlewareRegistry.register(store => next => action => {
    const result = next(action);

    switch (action.type) {
    case ENDPOINT_MESSAGE_RECEIVED: {
        const { data, participant } = action;

        if (data?.name === ENDPOINT_TEXT_MESSAGE_NAME && typeof data.text === 'string') {
            _onEndpointText(store, participant?.getId?.(), data.text);
        }
        break;
    }

    case SET_REMOTE_ANNOTATION_ALLOWED:
        getCurrentConference(store.getState())
            ?.setLocalParticipantProperty(ANNOTATION_OFF_PROPERTY, String(!action.allowed));
        action.allowed ? _toDesktopApp({ allowed: true }) : _clearRemote(store);
        break;

    case CLEAR_REMOTE_ANNOTATIONS:
        _clearRemote(store);
        break;

    case FAKE_PARTICPANT_LEFT:
        action.options?.fakeParticipant === 'LocalScreenShare' && _notified.clear();
        break;

    case CONFERENCE_LEFT:
        clearShare();
        _notified.clear();
        break;
    }

    return result;
});

/**
 * Handles an annotation or pointer message from another participant.
 *
 * @param {Store} store - The redux store.
 * @param {string} senderId - Who sent it.
 * @param {string} text - The endpoint text message.
 * @returns {void}
 */
function _onEndpointText(store, senderId, text) {
    let message;

    try {
        message = JSON.parse(text);
    } catch {
        return;
    }

    const { type, payload } = message || {};

    if ((type !== ANNOTATION_MESSAGE && type !== POINTER_MESSAGE) || !payload || !senderId) {
        return;
    }

    if (payload.type === 'clear') {
        // Only a sharer clears their own share. The desktop overlay's Clear
        // button sends no sharerId.
        (!payload.sharerId || payload.sharerId === senderId) && clearShare(senderId);

        return;
    }

    // What sharers draw on their own screen carries no sharerId: the desktop
    // app burns those strokes into the shared video already.
    const { sharerId } = payload;
    const state = store.getState();

    if (typeof sharerId !== 'string' || sharerId === senderId || !isRemoteAnnotationAllowed(state, sharerId)) {
        return;
    }

    // Name and color come from our own state, not from the message.
    const pointer = {
        sharerId,
        name: getParticipantDisplayName(state, senderId),
        color: getAnnotationColor(state, senderId)
    };

    switch (payload.type) {
    case 'stroke': {
        const points = readPoints(payload.points, MAX_POINTS_PER_MESSAGE);

        if (points) {
            const [ x, y ] = points[points.length - 1];

            addSegment({
                sharerId,
                senderId,
                points,
                color: pointer.color,
                name: pointer.name
            });
            setPointer(senderId, {
                ...pointer,
                x,
                y
            });
            _notifySharer(store, sharerId, senderId, pointer.name);
        }
        break;
    }

    case 'pointer': {
        const [ point ] = readPoints([ [ payload.x, payload.y ] ], 1) || [];

        point && setPointer(senderId, {
            ...pointer,
            x: point[0],
            y: point[1]
        });
        break;
    }

    case 'pointer-gone':
        removePointer(senderId);
        break;
    }
}

/**
 * Tells the local sharer, once per viewer and share, that someone draws on
 * their screen - with a way to see it from another window, or to stop it.
 *
 * @param {Store} store - The redux store.
 * @param {string} sharerId - The share drawn on.
 * @param {string} senderId - The viewer drawing.
 * @param {string} name - The viewer's display name.
 * @returns {void}
 */
function _notifySharer({ dispatch, getState }, sharerId, senderId, name) {
    if (sharerId !== getLocalParticipant(getState())?.id || _notified.has(senderId)) {
        return;
    }
    _notified.add(senderId);

    try {
        dispatch(showNotification({
            titleKey: 'screenAnnotation.someoneIsDrawing',
            titleArguments: { name },
            descriptionKey: 'screenAnnotation.someoneIsDrawingDescription',
            customActionNameKey: [ 'screenAnnotation.show', 'screenAnnotation.turnOff' ],
            customActionHandler: [
                () => {
                    dispatch(showLocalShareWithAnnotations());

                    return true;
                },
                () => {
                    dispatch(setRemoteAnnotationAllowed(false));

                    return true;
                }
            ]
        }, NOTIFICATION_TIMEOUT_TYPE.LONG));
    } catch {
        // showNotification throws when the deployment's config filters it out.
    }
}

/**
 * Wipes what viewers drew on the local share: on every viewer, here, and on
 * the desktop app's on-screen overlay.
 *
 * @param {Store} store - The redux store.
 * @returns {void}
 */
function _clearRemote({ getState }) {
    const state = getState();
    const localId = getLocalParticipant(state)?.id;

    sendAnnotationMessage(getCurrentConference(state), ANNOTATION_MESSAGE, {
        type: 'clear',
        sharerId: localId
    });
    clearShare(localId);
    _toDesktopApp({
        allowed: state['features/screen-annotation'].allowed,
        clear: true
    });
}

/**
 * Hands the sharer's choice to the desktop app, whose preload listens on this
 * window and filters what reaches its on-screen overlay. A no-op in browsers.
 *
 * @param {Object} message - { allowed, clear }.
 * @returns {void}
 */
function _toDesktopApp(message) {
    window.postMessage({
        type: DESKTOP_BRIDGE_MESSAGE,
        ...message
    }, window.location.origin);
}
